import React, { useState } from 'react';
import { motion, AnimatePresence } from '@/lib/motion';
import { Play, X, Buildings, House, Storefront } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { getSrcSet, SIZES } from '../utils/responsiveImage';

const API = process.env.REACT_APP_BACKEND_URL;
const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'https://files.ihgbrands.com/ihg-ind';

const videos = [
  {
    id: 'hotel-project',
    title: 'Al Mira Tower',
    subtitle: 'Hospitality Metalwork',
    description: 'Perforated bronze facade screens with mashrabiya patterns for a 5-star hotel in Dubai Marina.',
    icon: Buildings,
    videoUrl: `${MEDIA_URL}/generated/video_hotel.mp4`,
    thumbnail: `${MEDIA_URL}/generated/project_hotel_lobby.webp`,
    duration: '1:45',
    hasVideo: true
  },
  {
    id: 'villa-project',
    title: 'Villa Serena Estate',
    subtitle: 'Residential Metalwork',
    description: 'Ornate wrought iron gates, bronze balustrades, and custom metalwork for Emirates Hills villa.',
    icon: House,
    videoUrl: `${MEDIA_URL}/generated/video_villa.mp4`,
    thumbnail: `${MEDIA_URL}/generated/project_villa_emirates.webp`,
    duration: '1:30',
    hasVideo: true
  },
  {
    id: 'retail-project',
    title: 'Maison Lumière',
    subtitle: 'Luxury Retail Design',
    description: 'Brass and glass display systems with integrated LED lighting for a premium boutique.',
    icon: Storefront,
    videoUrl: `${MEDIA_URL}/generated/video_retail.mp4`,
    thumbnail: `${MEDIA_URL}/generated/project_jewellery_retail.webp`,
    duration: '1:20',
    hasVideo: true
  }
];

export const VideoShowcase = () => {
  const [activeVideo, setActiveVideo] = useState(null);

  const handleVideoClick = (video) => {
    if (video.hasVideo) {
      setActiveVideo(video);
    }
    // If no video, the Link component handles navigation
  };

  return (
    <>
      <section className="py-16 md:py-20 bg-obsidian">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 text-bronze text-[10px] font-body font-semibold uppercase tracking-[0.2em] mb-3">
              <span className="w-6 h-px bg-bronze" />
              VIDEO GALLERY
              <span className="w-6 h-px bg-bronze" />
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-tight">
              See Our Work in Motion
            </h2>
            <p className="font-body text-white/60 mt-3 max-w-xl mx-auto text-sm">
              Explore our craftsmanship through immersive video tours of completed projects.
            </p>
          </div>

          {/* Video Grid */}
          <div className="grid md:grid-cols-3 gap-6">
            {videos.map((video, index) => {
              const CardWrapper = video.hasVideo ? 'div' : Link;
              const cardProps = video.hasVideo 
                ? { onClick: () => handleVideoClick(video) }
                : { to: video.link };

              return (
                <motion.div
                  key={video.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <CardWrapper
                    {...cardProps}
                    className="group cursor-pointer block"
                    data-testid={`video-card-${video.id}`}
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-video overflow-hidden rounded-sm mb-4">
                      <img loading="lazy" decoding="async"
                        src={video.thumbnail}
                        srcSet={getSrcSet(video.thumbnail)}
                        sizes={SIZES.threeCol}
                        alt={video.title}
                        width={640}
                        height={360}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-obsidian/40 group-hover:bg-obsidian/20 transition-colors" />
                      
                      {/* Play Button */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          className="w-16 h-16 rounded-full bg-bronze/90 flex items-center justify-center group-hover:bg-bronze transition-colors"
                        >
                          <Play weight="fill" className="w-7 h-7 text-white ml-1" />
                        </motion.div>
                      </div>

                      {/* Duration Badge */}
                      <div className="absolute bottom-3 right-3 px-2 py-1 bg-obsidian/80 rounded text-[10px] font-bold text-white">
                        {video.duration}
                      </div>

                      {/* Icon Badge */}
                      <div className="absolute top-3 left-3 w-8 h-8 bg-bronze/90 rounded flex items-center justify-center">
                        <video.icon weight="bold" className="w-4 h-4 text-white" />
                      </div>
                    </div>

                    {/* Info */}
                    <h3 className="font-display text-lg font-bold text-white uppercase tracking-wide group-hover:text-bronze transition-colors">
                      {video.title}
                    </h3>
                    <p className="text-[11px] font-semibold text-bronze uppercase tracking-wider mt-1">
                      {video.subtitle}
                    </p>
                    <p className="text-xs text-white/50 mt-2 leading-relaxed line-clamp-2">
                      {video.description}
                    </p>
                  </CardWrapper>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
            onClick={() => setActiveVideo(null)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-obsidian/95" />

            {/* Modal Content */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setActiveVideo(null)}
                className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center text-white/70 hover:text-white transition-colors"
                aria-label="Close video"
                data-testid="video-modal-close"
              >
                <X weight="bold" className="w-6 h-6" />
              </button>

              {/* Video Title */}
              <div className="mb-4">
                <h3 className="font-display text-xl md:text-2xl font-bold text-white uppercase tracking-wide">
                  {activeVideo.title}
                </h3>
                <p className="text-sm text-bronze">{activeVideo.subtitle}</p>
              </div>

              {/* Video Player */}
              <div className="relative aspect-video bg-black rounded-sm overflow-hidden">
                <video
                  src={activeVideo.videoUrl}
                  controls
                  autoPlay
                  className="w-full h-full"
                  poster={activeVideo.thumbnail}
                >
                  Your browser does not support the video tag.
                </video>
              </div>

              {/* Description */}
              <p className="mt-4 text-sm text-white/60">
                {activeVideo.description}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default VideoShowcase;
